import ElementUtils from "@/utils/ElementUtils";
import {
  BLUR,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_FILL_TYPE,
  BUTTON_PADDING,
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_BORDER_COLOR,
  BUTTON_BORDER_WIDTH,
  BUTTON_BORDER_STYLE,
  BUTTON_BORDER_RADIUS,
  BUTTON_HOVER_DURATION,
  BUTTON_HOVER_BORDER_WIDTH,
  BUTTON_HOVER_BORDER_RADIUS,
  BUTTON_TEXT_COLOR,
  BUTTON_TEXT_FONT,
  BUTTON_TEXT_SIZE,
  BUTTON_TEXT_LETTER_SPACING,
  BUTTON_TEXT_LETTER_CASE,
  BUTTON_TEXT_LINE_HEIGHT,
  OPACITY,
  SHADOW_STYLE,
  BUTTON_GRADIENT_COLOR_1,
  BUTTON_GRADIENT_COLOR_2,
  BUTTON_GRADIENT_ROTATION,
  ELEMENT_BACKGROUND_FILL_TYPE,
  ELEMENT_BACKGROUND_COLOR,
  GRADIENT_COLOR_1,
  GRADIENT_COLOR_2,
  GRADIENT_ROTATION,
  BORDER_COLOR,
  BORDER_WIDTH,
  BORDER_RADIUS,
  PADDING,
  TEXT_COLOR,
  TEXT_FONT,
  TEXT_SIZE,
  TEXT_LETTER_SPACING,
  TEXT_LETTER_CASE,
  TEXT_LINE_HEIGHT,
  BUTTON_HOVER_BACKGROUND_COLOR,
  BUTTON_HOVER_FILL_TYPE,
  BUTTON_HOVER_GRADIENT_COLOR_1,
  BUTTON_HOVER_GRADIENT_COLOR_2,
  BUTTON_HOVER_GRADIENT_ROTATION,
  BUTTON_HOVER_BORDER_COLOR,
  BUTTON_HOVER_PADDING,
  BUTTON_HOVER_TEXT_COLOR,
  BUTTON_HOVER_TEXT_FONT,
  BUTTON_HOVER_TEXT_SIZE,
  BUTTON_HOVER_TEXT_LETTER_SPACING,
  BUTTON_HOVER_TEXT_LETTER_CASE,
  BUTTON_HOVER_TEXT_LINE_HEIGHT,
  HOVER_SHADOW_STYLE,
  HOVER_SHADOW_COLOR,
  HOVER_SHADOW_OFFSET_X,
  HOVER_SHADOW_OFFSET_Y,
  HOVER_SHADOW_BLUR,
  HOVER_OPACITY,
  HOVER_BLUR,
  HOVER,
  SHADOW_COLOR,
  SHADOW_OFFSET_X,
  SHADOW_OFFSET_Y,
  SHADOW_BLUR,
} from "@/constants/MergeConstants";
import { BASE_INPUT_CONFIG } from "./BaseInputConfigMap";
import { PREVIEWABLE_HANDLERS } from "./ElementInputConstants.mjs";
import { DEFAULT_EFFECTS_VALUES } from "./EffectsPropertyMaps";

/**
 * @typedef {import("immutable").Map} Map
 *
 * @typedef {Object} UpdateCallbackProps
 * @prop {CustomEvent} e
 * @prop {import("@/vo/BlockUpdate.mjs").PropUpdate} propInfo
 * @prop {Map} theme
 * @prop {Map} element
 */

/**
 * This min height just affects transformer sizing
 * @param {UpdateCallbackProps} props
 * @returns
 */
const handleMinimumSizeChange = ({ propInfo, theme, element }) =>
  ElementUtils.adjustSizeToMinIfNeeded(element, theme, propInfo);

export const DEFAULT_BUTTON_BORDER_VALUES = {
  [BUTTON_BORDER_COLOR]: "colors-0",
  [BUTTON_BORDER_WIDTH]: 0,
  [BUTTON_BORDER_STYLE]: "solid",
  [BUTTON_BORDER_RADIUS]: 0,
};

export const DEFAULT_BUTTON_SHADOW_VALUES = {
  [SHADOW_STYLE]: "none",
  [SHADOW_COLOR]: "colors-0:50",
  [SHADOW_OFFSET_X]: 2,
  [SHADOW_OFFSET_Y]: 2,
  [SHADOW_BLUR]: 6,
};

export const DEFAULT_BUTTON_TEXT_VALUES = {
  [BUTTON_TEXT_COLOR]: "colors-0",
  [BUTTON_TEXT_FONT]: "arial",
  [BUTTON_TEXT_SIZE]: 16,
  [BUTTON_TEXT_LETTER_SPACING]: 0,
  [BUTTON_TEXT_LETTER_CASE]: "none",
  [BUTTON_TEXT_LINE_HEIGHT]: 1,
};

export const DEFAULT_BUTTON_BACKGROUND_VALUES = {
  ...DEFAULT_EFFECTS_VALUES,
  [BUTTON_FILL_TYPE]: "solid",
  [BUTTON_BACKGROUND_COLOR]: "colors-0",
  [BUTTON_GRADIENT_COLOR_1]: "colors-0",
  [BUTTON_GRADIENT_COLOR_2]: "colors-1",
  [BUTTON_GRADIENT_ROTATION]: 0,
  [BUTTON_PADDING]: "6px 8px 6px 8px",
  [BUTTON_HEIGHT]: 40,
  [BUTTON_WIDTH]: 100,
  ...DEFAULT_BUTTON_BORDER_VALUES,
  ...DEFAULT_BUTTON_SHADOW_VALUES,
};

export const DEFAULT_BUTTON_VALUES = {
  ...DEFAULT_BUTTON_BACKGROUND_VALUES,
  ...DEFAULT_BUTTON_TEXT_VALUES,
};

const HOVER_OVERRIDES = {
  isSynced: true,
  canToggle: false,
};
/**
 * @typedef {Object} BaseInputMeta -
 * @param {string} type - Configures input type
 * @param {string} label - Input label
 * @param {Array<string>} inputEventHandlers - Named handlers to apply to the input
 *
 * @typedef {Object} NumberInputMeta
 *
 * @typedef {Object} TextInputMeta
 *
 */

export const BUTTON_PROPERTY_META_MAP = {
  // button min height and width
  [BUTTON_HEIGHT]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Height",
    min: 0,
    type: "number",
    units: "px",
  },
  [BUTTON_WIDTH]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    min: 0,
    label: "Width",
    type: "number",
    units: "px",
  },
  // fill
  [BUTTON_FILL_TYPE]: {
    ...BASE_INPUT_CONFIG[ELEMENT_BACKGROUND_FILL_TYPE],
    label: "Background",
    showAdvancedFill: false,
  },
  [BUTTON_HOVER_FILL_TYPE]: {
    ...BASE_INPUT_CONFIG[ELEMENT_BACKGROUND_FILL_TYPE],
    label: "Background",
    showAdvancedFill: false,
    children: [
      BUTTON_HOVER_BACKGROUND_COLOR,
      BUTTON_HOVER_GRADIENT_COLOR_1,
      BUTTON_HOVER_GRADIENT_COLOR_2,
      BUTTON_HOVER_GRADIENT_ROTATION,
    ],
    ...HOVER_OVERRIDES,
    disabled: true,
    forceContextEnabled: true,
  },
  [BUTTON_BACKGROUND_COLOR]: {
    ...BASE_INPUT_CONFIG[ELEMENT_BACKGROUND_COLOR],
    label: "Color",
  },
  [BUTTON_HOVER_BACKGROUND_COLOR]: {
    ...BASE_INPUT_CONFIG[ELEMENT_BACKGROUND_COLOR],
    label: "Color",
  },
  [BUTTON_GRADIENT_COLOR_1]: {
    ...BASE_INPUT_CONFIG[GRADIENT_COLOR_1],
  },
  [BUTTON_HOVER_GRADIENT_COLOR_1]: {
    ...BASE_INPUT_CONFIG[GRADIENT_COLOR_1],
  },
  [BUTTON_GRADIENT_COLOR_2]: {
    ...BASE_INPUT_CONFIG[GRADIENT_COLOR_2],
  },
  [BUTTON_HOVER_GRADIENT_COLOR_2]: {
    ...BASE_INPUT_CONFIG[GRADIENT_COLOR_2],
  },
  [BUTTON_GRADIENT_ROTATION]: {
    ...BASE_INPUT_CONFIG[GRADIENT_ROTATION],
  },
  [BUTTON_HOVER_GRADIENT_ROTATION]: {
    ...BASE_INPUT_CONFIG[GRADIENT_ROTATION],
  },
  // border
  [BUTTON_BORDER_COLOR]: {
    ...BASE_INPUT_CONFIG[BORDER_COLOR],
  },
  [BUTTON_HOVER_BORDER_COLOR]: {
    ...BASE_INPUT_CONFIG[BORDER_COLOR],
    ...HOVER_OVERRIDES,
  },
  [BUTTON_BORDER_WIDTH]: {
    ...BASE_INPUT_CONFIG[BORDER_WIDTH],
    updateCallback: handleMinimumSizeChange,
    label: "Border Size",
  },
  [BUTTON_HOVER_BORDER_WIDTH]: {
    ...BASE_INPUT_CONFIG[BORDER_WIDTH],
    ...HOVER_OVERRIDES,
    label: "Border Size",
    // todo: figure the min out. Does an exponential increase at the moment when enabled.
    // updateCallback: handleMinimumSizeChange,
  },
  [BUTTON_BORDER_RADIUS]: {
    ...BASE_INPUT_CONFIG[BORDER_RADIUS],
  },
  [BUTTON_HOVER_BORDER_RADIUS]: {
    ...BASE_INPUT_CONFIG[BORDER_RADIUS],
    ...HOVER_OVERRIDES,
  },
  // padding
  [BUTTON_PADDING]: {
    ...BASE_INPUT_CONFIG[PADDING],
    updateCallback: handleMinimumSizeChange,
  },
  [BUTTON_HOVER_PADDING]: {
    ...BASE_INPUT_CONFIG[PADDING],
    ...HOVER_OVERRIDES,
    updateCallback: handleMinimumSizeChange,
  },
  // text
  [BUTTON_TEXT_COLOR]: {
    ...BASE_INPUT_CONFIG[TEXT_COLOR],
  },
  [BUTTON_HOVER_TEXT_COLOR]: {
    ...BASE_INPUT_CONFIG[TEXT_COLOR],
    ...HOVER_OVERRIDES,
  },
  [BUTTON_TEXT_FONT]: {
    ...BASE_INPUT_CONFIG[TEXT_FONT],
  },
  [BUTTON_HOVER_TEXT_FONT]: {
    ...BASE_INPUT_CONFIG[TEXT_FONT],
    ...HOVER_OVERRIDES,
  },
  [BUTTON_TEXT_SIZE]: {
    ...BASE_INPUT_CONFIG[TEXT_SIZE],
    updateCallback: handleMinimumSizeChange,
  },
  [BUTTON_HOVER_TEXT_SIZE]: {
    ...BASE_INPUT_CONFIG[TEXT_SIZE],
    ...HOVER_OVERRIDES,
    updateCallback: handleMinimumSizeChange,
  },
  [BUTTON_TEXT_LETTER_SPACING]: {
    ...BASE_INPUT_CONFIG[TEXT_LETTER_SPACING],
  },
  [BUTTON_HOVER_TEXT_LETTER_SPACING]: {
    ...BASE_INPUT_CONFIG[TEXT_LETTER_SPACING],
    ...HOVER_OVERRIDES,
  },
  [BUTTON_TEXT_LETTER_CASE]: {
    ...BASE_INPUT_CONFIG[TEXT_LETTER_CASE],
  },
  [BUTTON_HOVER_TEXT_LETTER_CASE]: {
    ...BASE_INPUT_CONFIG[TEXT_LETTER_CASE],
    ...HOVER_OVERRIDES,
  },
  [BUTTON_TEXT_LINE_HEIGHT]: {
    ...BASE_INPUT_CONFIG[TEXT_LINE_HEIGHT],
    updateCallback: handleMinimumSizeChange,
  },
  [BUTTON_HOVER_TEXT_LINE_HEIGHT]: {
    ...BASE_INPUT_CONFIG[TEXT_LINE_HEIGHT],
    ...HOVER_OVERRIDES,
    updateCallback: handleMinimumSizeChange,
  },
  // effect
  [OPACITY]: {
    ...BASE_INPUT_CONFIG[OPACITY],
  },
  [HOVER_OPACITY]: {
    ...BASE_INPUT_CONFIG[OPACITY],
    ...HOVER_OVERRIDES,
  },
  [BLUR]: {
    ...BASE_INPUT_CONFIG[BLUR],
  },
  [HOVER_BLUR]: {
    ...BASE_INPUT_CONFIG[BLUR],
    ...HOVER_OVERRIDES,
  },
  // shadow
  [SHADOW_STYLE]: {
    ...BASE_INPUT_CONFIG[SHADOW_STYLE],
  },
  [SHADOW_COLOR]: {
    ...BASE_INPUT_CONFIG[SHADOW_COLOR],
  },
  [SHADOW_OFFSET_X]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_X],
  },
  [SHADOW_OFFSET_Y]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_Y],
  },
  [SHADOW_BLUR]: {
    ...BASE_INPUT_CONFIG[SHADOW_BLUR],
  },
  [HOVER_SHADOW_STYLE]: {
    ...BASE_INPUT_CONFIG[SHADOW_STYLE],
    ...HOVER_OVERRIDES,
    children: [HOVER_SHADOW_COLOR, HOVER_SHADOW_OFFSET_X, HOVER_SHADOW_OFFSET_Y, HOVER_SHADOW_BLUR],
  },
  [HOVER_SHADOW_COLOR]: {
    ...BASE_INPUT_CONFIG[SHADOW_COLOR],
  },
  [HOVER_SHADOW_OFFSET_X]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_X],
  },
  [HOVER_SHADOW_OFFSET_Y]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_Y],
  },
  [HOVER_SHADOW_BLUR]: {
    ...BASE_INPUT_CONFIG[SHADOW_BLUR],
  },
  // hover specific
  [BUTTON_HOVER_DURATION]: {
    inputEventHandlers: [...PREVIEWABLE_HANDLERS],
    label: "Hover Duration",
    min: 0,
    step: "0.25",
    type: "number",
    units: "s",
  },
};

export const BUTTON_PROPERTY_ORDER = [
  BUTTON_FILL_TYPE,
  BUTTON_BORDER_COLOR,
  BUTTON_BORDER_WIDTH,
  BUTTON_TEXT_COLOR,
  BUTTON_TEXT_FONT,
  BUTTON_TEXT_SIZE,
  BUTTON_TEXT_LETTER_SPACING,
  BUTTON_TEXT_LETTER_CASE,
  BUTTON_PADDING,
  BUTTON_TEXT_LINE_HEIGHT,
  BUTTON_BORDER_RADIUS,
  SHADOW_STYLE,
  OPACITY,
  BLUR,
];

export const BUTTON_HOVER_PROPERTY_ORDER = [
  BUTTON_HOVER_DURATION,
  ...BUTTON_PROPERTY_ORDER.map((propName) => `${HOVER}.${propName}`),
];
